import { Typography, MediaImageStandard } from 'components/Contentful'
import Section from 'components/Section/Section'
import { Divider } from '@achieve/ascend'
import { useMemoizedContentGetter } from 'utils/contentful'
import classNames from 'classnames'

import LoadingTerms from './LoadingTerms'
import { useHELOCCalculator } from './useHELOCCalculator'
import { AmountSlider } from './AmountSlider'
import { TermSlider } from './TermSlider'
import { EstimatedApr } from './EstimatedApr'
import { MonthlyPayment } from './MonthlyPayment'
import { BarGraphContent } from './BarGraphContent'
import { TitleAndSubtitle } from './TitleAndSubtitle'
import { NegativeEquityUpdated, NegativeEquityInitial } from './NegativeEquityView'

function HELOCTerms({ content, helocOutput, setEquityError, helocUpdated, recalculate, ref }) {
  const {
    config,
    button,
    disclaimer,
    unlicensedButton,
    headerImage,
    unlicensedImage,
    equityImage,
  } = useMemoizedContentGetter(content, [
    'titleOutput',
    'subTitleOutput',
    'config',
    'button',
    'disclaimer',
    'unlicensedButton',
    'headerImage',
    'unlicensedImage',
    'equityImage',
  ])
  const jsonConfig = config?.jsonContent || {}

  const {
    isLoading,
    licensedFlag,
    term,
    customAPR,
    loanPayments,
    modalStatus,
    setModalStatus,
    sliderAmountConfig,
    debouncedSliderAmount,
    negativeEquity,
    negativeEquityConfig,
    chartData,
    finalTitle,
    finalSubTitle,
    negativeEquityTitleSubtitle,
    handleReleaseAmount,
    handleChangeTerm,
    handleChangeCustomAPR,
  } = useHELOCCalculator({ helocOutput, setEquityError, recalculate, jsonConfig })

  const sliderProps = {
    jsonConfig,
    modalStatus,
    setModalStatus,
    debouncedSliderAmount,
    sliderAmountConfig,
    helocUpdated,
    handleReleaseAmount,
  }

  const termProps = { jsonConfig, loanPayments, term, handleChangeTerm }

  const aprProps = {
    jsonConfig,
    licensedFlag,
    loanPayments,
    term,
    customAPR,
    helocUpdated,
    handleChangeCustomAPR,
    handleReleaseAmount,
  }

  const monthlyProps = { jsonConfig, loanPayments, term }

  const barGraphProps = {
    helocUpdated,
    chartData,
    jsonConfig,
    licensedFlag,
    button: { ...button, linkHref: button?.linkHref + `&loan_amount=${debouncedSliderAmount}` },
    unlicensedButton,
    disclaimer,
    negativeEquityConfig,
  }

  const titleProps = { finalTitle, finalSubTitle, sliderAmountConfig }

  if (isLoading) {
    return (
      <div className="w-full" ref={ref}>
        <LoadingTerms helocUpdated={helocUpdated} />
      </div>
    )
  }

  if (negativeEquity) {
    return helocUpdated ? (
      <NegativeEquityUpdated
        equityImage={equityImage}
        negativeEquityTitleSubtitle={negativeEquityTitleSubtitle}
        negativeEquityConfig={negativeEquityConfig}
      />
    ) : (
      <NegativeEquityInitial
        negativeEquityTitleSubtitle={negativeEquityTitleSubtitle}
        negativeEquityConfig={negativeEquityConfig}
      />
    )
  }

  if (!helocUpdated) {
    return (
      <Section backgroundColor={jsonConfig?.pageSectionBackgroundColor} className="py-10" ref={ref}>
        <div className="flex flex-col text-center">
          <TitleAndSubtitle {...titleProps} />
        </div>

        <div className="flex flex-col xl:grid xl:grid-cols-11 xl:gap-8 border-[#C0CBD8] border-solid border p-6 sm:border-none sm:border sm:p-0 rounded-3xl">
          <div className="xl:col-span-5 flex flex-col">
            <div className="w-full p-2">
              <AmountSlider {...sliderProps} />
              <Divider className="mx-0 my-6 border-[#E7ECF3]" />
              <TermSlider {...termProps} />
              <Divider className="mx-0 my-6 border-[#E7ECF3]" />
              <EstimatedApr {...aprProps} />
              <Divider className="mx-0 my-6 border-[#E7ECF3]" />
              <MonthlyPayment {...monthlyProps} />
            </div>
          </div>
          <div className="xl:col-span-5 flex flex-col sm:p-8">
            <div className="w-full mx-auto rounded-3xl overflow-hidden border-[#C0CBD8] sm:border-solid sm:border sm:p-6">
              <BarGraphContent {...barGraphProps} />
            </div>
          </div>
        </div>
      </Section>
    )
  }

  return (
    <Section
      backgroundColor={jsonConfig?.pageSectionBackgroundColor}
      className="w-full mt-6 lg:mt-0"
      ref={ref}
    >
      <div className="flex flex-col border-[#C0CBD8] border-solid border sm:border-none sm:border rounded-3xl">
        <div className="w-full mx-auto rounded-3xl overflow-hidden border-[#C0CBD8] sm:border-solid sm:border">
          <div className="flex flex-col items-center bg-[#3B6AF7]" ref={ref}>
            {headerImage ? (
              <>
                <MediaImageStandard
                  className="relative top-0 right-0 w-[105%]"
                  alt={'confetti'}
                  content={headerImage.mediaContent}
                  layout="fixed"
                />
                <TitleAndSubtitle {...titleProps} padding={null} color={false} />
              </>
            ) : (
              <TitleAndSubtitle {...titleProps} padding={null} color={false} />
            )}
          </div>
          <div className="mr-0 ml-0 flex flex-col w-full sm:flex-row justify-between bg-white px-4 pt-4">
            <div className="flex flex-col min-w-[45%] sm:pl-4">
              <div className="pb-6 pr-2 pl-2">
                <AmountSlider {...sliderProps} />
              </div>
              <div className="hidden sm:block">
                <EstimatedApr {...aprProps} />
              </div>
            </div>
            <div className="flex flex-col min-w-[45%] pr-4 sm:mt-0 bg-white">
              <div className="pb-6 pr-2 pl-2">
                <TermSlider {...termProps} />
              </div>
              <div className="hidden sm:block">
                <MonthlyPayment {...monthlyProps} />
              </div>
              <div className="flex flex-row justify-between sm:hidden ">
                <div className="flex flex-col">
                  <EstimatedApr {...aprProps} variant="bodySm" />
                </div>
                <div className="flex flex-col">
                  <MonthlyPayment {...monthlyProps} variant="bodySm" />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white px-4 sm:mt-0 -mt-8">
            <BarGraphContent {...barGraphProps} buttonWidth="false" />
          </div>
        </div>
      </div>
      {unlicensedButton && !licensedFlag && helocUpdated ? (
        <div className="flex flex-col items-center justify-center border-solid border-2 border-[#D2E2FE] rounded-[20px] mt-8 bg-[#F5F9FF]">
          <div className="flex sm:flex-row flex-col w-full pt-8 pb-6 items-left sm:items-center justify-center pr-8 pl-8">
            <div classNames="mr-0 sm:mr-6">
              <MediaImageStandard
                className="h-[48px] w-[48px]"
                alt={unlicensedImage.altText}
                content={unlicensedImage.mediaContent}
                layout="fit"
              />
            </div>
            <div className="flex flex-col items-start ml-0 sm:ml-8">
              <div className="mb-4">
                <Typography
                  fontWeight="bold"
                  className={classNames(
                    'text-left text-[1.4rem] leading-[100%] lg:leading-[100%] lg:tracking-[-0.01em]'
                  )}
                  content="Don't qualify for a HELOC in your state? We can help."
                />
              </div>
              <Typography
                className={classNames(
                  'text-left text-[1rem] leading-[100%] lg:leading-[100%] lg:tracking-[-0.01em]'
                )}
                content="Save time with a free, no-obligation consultation to go over all your options."
              />
            </div>
          </div>
        </div>
      ) : null}
    </Section>
  )
}

export { HELOCTerms }
export default HELOCTerms
